import { Button, Dropdown, MenuProps, Space, Spin, Typography } from "antd";
import { BoardResource } from "dtos/kanban.dto";
import { Resource, Stage } from "dtos/stage.dto";
import { resolveStageColor, resolveStageIcon } from "components/Stages/constants";
import { TbRocket, TbX } from "react-icons/tb";
import { ApiService } from "api/api.service";
import { useMemo } from "react";

const BOARD_RESOURCE_TO_STAGE_RESOURCE: Partial<Record<BoardResource, Resource>> = {
  [BoardResource.APPLICATION]: Resource.APPLICATION,
  [BoardResource.LOAN]: Resource.LOAN,
};

type ActionItem = { label: React.ReactNode; value: string };
type ActionGroup = { group: string; shortPrefix?: string; items: ActionItem[] };
export type ActionGroups = ActionGroup[];

export const buildActionGroups = (stages: Stage[], resource: BoardResource): ActionGroups => {
  const stageResource = BOARD_RESOURCE_TO_STAGE_RESOURCE[resource];
  if (!stageResource) return [];

  const filtered = stages.filter((s) => s.resource === stageResource && !s.archived);
  if (!filtered.length) return [];

  return [
    {
      group: "Triggers",
      shortPrefix: "Stage",
      items: filtered.map((stage) => {
        const Icon = resolveStageIcon(stage.icon);
        const swatch = resolveStageColor(stage.color);

        return {
          value: `${stage.resource}:${stage.value}`,
          label: (
            <Space size={6}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: 4,
                  background: swatch.bg,
                  color: swatch.text,
                  boxShadow: `inset 0 0 0 1px ${swatch.border}`,
                  flexShrink: 0,
                }}
              >
                <Icon size={12} />
              </span>
              <span>{stage.label}</span>
            </Space>
          ),
        };
      }),
    },
  ];
};

export const findAction = (groups: ActionGroups, value?: string): { group: ActionGroup; item: ActionItem } | null => {
  if (!value) return null;
  for (const grp of groups) {
    const item = grp.items.find((i) => i.value === value);
    if (item) return { group: grp, item };
  }
  return null;
};

export const resolveActionLabel = (stages: Stage[], actionValue: string): string | null => {
  const stageValue = actionValue.split(":")[1];
  if (!stageValue) return null;
  return stages.find((s) => s.value === stageValue)?.label ?? null;
};

interface ActionPickerProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  resource?: BoardResource;
  orgCode?: string;
}

export const ActionPicker = ({ value, onChange, resource, orgCode }: ActionPickerProps) => {
  const stagesQuery = ApiService.organisation.useGetAllStages(orgCode);

  const groups = useMemo(() => {
    if (!resource || !stagesQuery.data) return [];
    return buildActionGroups(stagesQuery.data, resource);
  }, [stagesQuery.data, resource]);

  const selected = useMemo(() => findAction(groups, value), [groups, value]);

  // Resolve the selected stage's color for the trigger button accent
  const selectedSwatch = useMemo(() => {
    if (!value || !stagesQuery.data) return null;
    const stageValue = value.split(":")[1];
    const stage = stagesQuery.data.find((s) => s.value === stageValue);
    return stage ? resolveStageColor(stage.color) : null;
  }, [value, stagesQuery.data]);

  const selectedIcon = useMemo(() => {
    if (!value || !stagesQuery.data) return null;
    const stageValue = value.split(":")[1];
    const stage = stagesQuery.data.find((s) => s.value === stageValue);
    return stage ? resolveStageIcon(stage.icon) : null;
  }, [value, stagesQuery.data]);

  if (!resource || !BOARD_RESOURCE_TO_STAGE_RESOURCE[resource]) {
    return <span style={{ display: "inline-block", width: 0 }} />;
  }

  const items: MenuProps["items"] = [
    ...(selected
      ? ([
          { key: "__clear__", label: "Clear selection", icon: <TbX size={14} /> },
          { type: "divider" as const },
        ] satisfies MenuProps["items"])
      : []),
    ...groups.map((g) => ({
      key: `group:${g.group}`,
      type: "group" as const,
      label: g.group,
      children: g.items.map((i) => ({ key: i.value, label: i.label })),
    })),
  ];

  const handleClick: MenuProps["onClick"] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    onChange?.(key === "__clear__" ? undefined : key);
  };

  const isLoading = stagesQuery.isLoading;
  const SelectedIcon = selectedIcon;

  return (
    <Dropdown
      menu={{
        items,
        onClick: handleClick,
        selectable: true,
        selectedKeys: value ? [value] : [],
        style: { maxHeight: 300, overflowY: "auto" },
      }}
      trigger={["click"]}
      placement="bottomRight"
      disabled={isLoading}
    >
      <span style={{ display: "inline-flex", alignItems: "center", cursor: isLoading ? "wait" : "pointer" }}>
        {isLoading ? (
          <Spin size="small" />
        ) : selected && selectedSwatch ? (
          <Button
            size="small"
            type="text"
            style={{
              height: 22,
              padding: "0 6px",
              background: selectedSwatch.bg,
              border: `1px solid ${selectedSwatch.border}`,
              color: selectedSwatch.text,
              fontSize: 12,
              fontWeight: 500,
              maxWidth: 160,
            }}
          >
            <Space size={4} style={{ maxWidth: "100%" }}>
              {SelectedIcon && <SelectedIcon size={12} style={{ flexShrink: 0 }} />}
              <Typography.Text ellipsis style={{ fontSize: 12, color: selectedSwatch.text, maxWidth: 110 }}>
                {resolveActionLabel(stagesQuery.data ?? [], value!)}
              </Typography.Text>
            </Space>
          </Button>
        ) : (
          <Button type="text" size="small" icon={<TbRocket size={16} className="text-gray-500" />} />
        )}
      </span>
    </Dropdown>
  );
};
